import { useState } from "react";
import { Alert, Button, Input, Space, Spin, Typography } from "antd";
import { t } from "i18next";
import ApiService from "../../core/service/ApiService";
import NotificationService from "../../core/service/NotificationService";

const { Text, Paragraph } = Typography;

// PAVO response'u içinde fingerprint alanını derinlemesine ara
function extractFingerprint(obj) {
    if (!obj || typeof obj !== 'object') return '';
    for (const k of Object.keys(obj)) {
        if (k.toLowerCase() === 'fingerprint' && typeof obj[k] === 'string' && obj[k]) return obj[k];
    }
    for (const k of Object.keys(obj)) {
        if (obj[k] && typeof obj[k] === 'object') {
            const found = extractFingerprint(obj[k]);
            if (found) return found;
        }
    }
    return '';
}

export default function PosPairingModal({ device, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [requestBody, setRequestBody] = useState(null);
    const [responseText, setResponseText] = useState(null);
    const [error, setError] = useState(null);
    const [fingerprint, setFingerprint] = useState('');

    const doPair = async () => {
        setLoading(true);
        setError(null);
        setResponseText(null);
        setFingerprint('');
        try {
            const reqRes = await ApiService.FirmPosDevice.GetPairingRequest(device.id);
            if (!reqRes.success) return;
            setRequestBody(reqRes.data);

            let raw = null;
            let posData = null;
            try {
                const posRes = await fetch(`${device.address}/Pairing`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: reqRes.data,
                });
                raw = await posRes.text();
                setResponseText(raw);
                if (!posRes.ok) {
                    setError(`${t('POS device returned an error')} (HTTP ${posRes.status})`);
                }
            } catch (e) {
                setError(`${t('POS device is not reachable!')} (${e?.message || e})`);
                return;
            }

            try { posData = JSON.parse(raw); } catch (e) { posData = null; }
            if (posData) {
                if (posData.HasError) {
                    setError(`${t('POS device returned an error')}${posData.Message ? `: ${posData.Message}` : ''}`);
                }
                const fp = posData?.TransactionHandle?.Fingerprint || extractFingerprint(posData);
                if (fp) setFingerprint(fp);
            }
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        if (!fingerprint || !fingerprint.trim()) {
            NotificationService.error(t('Fingerprint is empty.'));
            return;
        }
        setSaving(true);
        try {
            const res = await ApiService.FirmPosDevice.SavePairing(device.id, {
                fingerprint: fingerprint.trim(),
                response: responseText,
            });
            if (res.success) onSuccess && onSuccess();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <Paragraph>
                <Text strong>{device.name}</Text> — <Text type="secondary">{device.address}</Text>
            </Paragraph>

            <Space style={{ marginBottom: 12 }}>
                <Button type="primary" loading={loading} onClick={doPair}>{t('Send Pairing Request')}</Button>
            </Space>

            {loading && <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>}

            {requestBody && (
                <>
                    <Text type="secondary">{t('Request')}:</Text>
                    <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{requestBody}</pre>
                </>
            )}

            {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}

            {responseText && (
                <>
                    <Text type="secondary">{t('Response')}:</Text>
                    <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{responseText}</pre>
                </>
            )}

            {responseText && (
                <div style={{ marginTop: 12 }}>
                    <Text>{t('Fingerprint')}</Text>
                    <Input.TextArea
                        value={fingerprint}
                        onChange={(e) => setFingerprint(e.target.value)}
                        rows={3}
                        placeholder={t('Paste / confirm the fingerprint to save')}
                        style={{ marginTop: 4 }}
                    />
                    <div style={{ textAlign: 'right', marginTop: 8 }}>
                        <Button type="primary" loading={saving} onClick={save} disabled={!fingerprint || !fingerprint.trim()}>
                            {t('Save Fingerprint')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
